// ─── Central Legal Content Source ───────────────────────────────────────────
// All legal text, company data, and cookie labels live here.
// Edit this ONE file to update legal content across the entire site.

export const companyInfo = {
  serviceName: "KitchenFlow",
  companyName: "JB Consulting Handelsbolag",
  orgNumber: "969804-4329",
  address: "[FÖRETAGSADRESS]",
  email: "kitchenflow.contact@gmail.com",
  // No phone number — intentionally omitted
} as const;

export const legalMeta = {
  lastUpdated: "2025-04-23",
  consentStorageKey: "kitchenflow_cookie_consent_v1",
  consentVersion: 1,
} as const;

export const legalRoutes = {
  terms: "/anvandarvillkor",
  privacy: "/integritetspolicy",
  cookies: "/cookiepolicy",
} as const;

export const footerLinks = {
  product: [
    { label: "Funktioner", href: "/#funktioner" },
    { label: "Hur det fungerar", href: "/#hur-det-fungerar" },
    { label: "Boka demo", href: "/#boka" },
  ],
  legal: [
    { label: "Användarvillkor", href: legalRoutes.terms },
    { label: "Integritetspolicy", href: legalRoutes.privacy },
    { label: "Cookiepolicy", href: legalRoutes.cookies },
  ],
} as const;

// ─── Cookie Categories ─────────────────────────────────────────────────────

export const cookieCategories = [
  {
    id: "necessary" as const,
    label: "Nödvändiga",
    description:
      "Dessa cookies krävs för att webbplatsen ska fungera korrekt. De kan inte inaktiveras.",
    locked: true,
  },
  {
    id: "statistics" as const,
    label: "Statistik",
    description:
      "Hjälper oss förstå hur besökare interagerar med webbplatsen genom att samla in anonym statistik.",
    locked: false,
  },
  {
    id: "marketing" as const,
    label: "Marknadsföring",
    description:
      "Används för att visa relevanta annonser och mäta effektiviteten av marknadsföringskampanjer.",
    locked: false,
  },
] as const;

export const cookieBannerContent = {
  title: "Vi värnar om din integritet",
  description:
    "Vi använder cookies för att förbättra din upplevelse. Du kan acceptera alla cookies, bara de nödvändiga, eller anpassa dina val.",
  acceptAll: "Acceptera alla",
  acceptNecessary: "Endast nödvändiga",
  customize: "Anpassa",
  settingsTitle: "Cookieinställningar",
  settingsDescription:
    "Välj vilka typer av cookies du vill tillåta. Nödvändiga cookies kan inte inaktiveras eftersom de krävs för att webbplatsen ska fungera.",
  save: "Spara inställningar",
} as const;

// ─── Legal Section Type ─────────────────────────────────────────────────────

export interface LegalSectionData {
  id: string;
  title: string;
  content: string[];
}

// ─── Terms of Use (/anvandarvillkor) ────────────────────────────────────────

export const termsSections: LegalSectionData[] = [
  {
    id: "om-tjansten",
    title: "1. Om tjänsten",
    content: [
      `${companyInfo.serviceName} är en digital menyhanteringsplattform som tillhandahålls av ${companyInfo.companyName} (org.nr ${companyInfo.orgNumber}), med adress ${companyInfo.address}. Tjänsten riktar sig till restauranger och livsmedelsverksamheter och möjliggör strukturerad hantering av menyer, rätter, komponenter, ingredienser och leverantörer.`,
    ],
  },
  {
    id: "vem-som-far-anvanda",
    title: "2. Vem som får använda tjänsten",
    content: [
      `Tjänsten är avsedd för företag och organisationer inom restaurang- och livsmedelsbranschen. Genom att använda ${companyInfo.serviceName} bekräftar du att du agerar på uppdrag av en juridisk person och att du har befogenhet att ingå avtal för dennes räkning.`,
    ],
  },
  {
    id: "tillaten-anvandning",
    title: "3. Tillåten användning",
    content: [
      `Tjänsten får enbart användas för sitt avsedda ändamål: hantering av menydata och relaterade arbetsflöden. Du får inte använda tjänsten för olagliga ändamål, försöka kringgå säkerhetsåtgärder, eller överlåta ditt konto till tredje part utan skriftligt samtycke från oss.`,
    ],
  },
  {
    id: "konton-och-inloggning",
    title: "4. Konton och inloggning",
    content: [
      `Användarkonton skapas av ${companyInfo.serviceName} åt kunden. Inloggningsuppgifter är personliga och ska behandlas konfidentiellt. Kunden ansvarar för att alla som tilldelas tillgång till kontot följer dessa villkor. Vi förbehåller oss rätten att inaktivera konton vid misstänkt missbruk.`,
    ],
  },
  {
    id: "kundens-ansvar",
    title: "5. Kundens ansvar",
    content: [
      `Kunden ansvarar för att den data som matas in i tjänsten – inklusive menyinformation, ingredienser, leverantörsuppgifter och annan relaterad information – är korrekt och laglig. Kunden är också ansvarig för att personuppgifter som eventuellt förekommer i den data som matas in behandlas i enlighet med tillämplig dataskyddslagstiftning.`,
    ],
  },
  {
    id: "personuppgifter",
    title: "6. Personuppgifter i tjänsten",
    content: [
      `Vi behandlar personuppgifter i enlighet med vår integritetspolicy. Se vår fullständiga integritetspolicy för detaljer om hur dina uppgifter hanteras, vilka rättigheter du har, och hur du kan kontakta oss.`,
    ],
  },
  {
    id: "immateriella-rattigheter",
    title: "7. Immateriella rättigheter",
    content: [
      `Alla rättigheter till ${companyInfo.serviceName} – inklusive programvara, design, logotyper och dokumentation – tillhör ${companyInfo.companyName}. Kunden beviljas en icke-exklusiv, icke-överlåtbar rätt att använda tjänsten under avtalstiden. Innehåll och data som kunden matar in i tjänsten tillhör kunden.`,
    ],
  },
  {
    id: "tillganglighet-och-andringar",
    title: "8. Tillgänglighet och ändringar",
    content: [
      `Vi strävar efter att hålla tjänsten tillgänglig dygnet runt, men kan inte garantera avbrottsfri tillgång. Vi förbehåller oss rätten att göra ändringar i tjänstens funktionalitet, design och villkor. Vid väsentliga förändringar i dessa villkor kommer vi att meddela berörda kunder.`,
    ],
  },
  {
    id: "support",
    title: "9. Support",
    content: [
      `Support tillhandahålls via e-post till ${companyInfo.email}. Vi strävar efter att besvara ärenden inom rimlig tid under vardagar.`,
    ],
  },
  {
    id: "avgifter-och-betalning",
    title: "10. Avgifter och betalning",
    content: [
      `Betalning för tjänsten sker genom fakturering enligt de villkor som överenskommits mellan ${companyInfo.companyName} och kunden. Betalningsvillkor, inklusive faktureringsperiod och betalningsfrist, framgår av det individuella avtalet eller den faktura som skickas till kunden.`,
    ],
  },
  {
    id: "avstangning-och-uppsagning",
    title: "11. Avstängning och uppsägning",
    content: [
      `Vi förbehåller oss rätten att stänga av eller avsluta ett konto om kunden väsentligen bryter mot dessa villkor. Kunden kan begära uppsägning av sitt konto genom att kontakta oss via ${companyInfo.email}. Vid uppsägning raderas kundens data inom rimlig tid, om inte lagkrav kräver fortsatt lagring.`,
    ],
  },
  {
    id: "ansvarsbegransning",
    title: "12. Ansvarsbegränsning",
    content: [
      `${companyInfo.companyName} ansvarar inte för indirekta skador, utebliven vinst eller förlust av data, utom vid grov vårdslöshet eller uppsåt. Vårt sammanlagda ansvar är begränsat till det belopp kunden betalat för tjänsten under de senaste tolv (12) månaderna.`,
    ],
  },
  {
    id: "force-majeure",
    title: "13. Force majeure",
    content: [
      `Ingen part är ansvarig för underlåtenhet att fullgöra sina skyldigheter om detta beror på omständigheter utanför partens rimliga kontroll, såsom naturkatastrofer, krig, pandemi, myndighetsåtgärder, strömavbrott eller allvarliga störningar i internetförbindelser.`,
    ],
  },
  {
    id: "tillamlig-lag-och-tvist",
    title: "14. Tillämplig lag och tvist",
    content: [
      `Dessa villkor regleras av svensk lag. Tvister som uppstår i anslutning till dessa villkor ska i första hand lösas genom förhandling mellan parterna. Om parterna inte kan nå en överenskommelse ska tvisten avgöras av svensk allmän domstol.`,
    ],
  },
  {
    id: "kontakt",
    title: "15. Kontakt",
    content: [
      `Vid frågor om dessa användarvillkor, kontakta oss på:`,
      `${companyInfo.companyName}\nOrg.nr ${companyInfo.orgNumber}\nAdress: ${companyInfo.address}\nE-post: ${companyInfo.email}`,
    ],
  },
];

// ─── Privacy Policy (/integritetspolicy) ────────────────────────────────────

export const privacySections: LegalSectionData[] = [
  {
    id: "inledning",
    title: "1. Inledning",
    content: [
      `Denna integritetspolicy beskriver hur ${companyInfo.companyName} (org.nr ${companyInfo.orgNumber}) behandlar personuppgifter i samband med tjänsten ${companyInfo.serviceName} och vår webbplats. Vi värnar om din integritet och behandlar personuppgifter i enlighet med EU:s dataskyddsförordning (GDPR) och tillämplig svensk dataskyddslagstiftning.`,
    ],
  },
  {
    id: "personuppgiftsansvarig",
    title: "2. Personuppgiftsansvarig",
    content: [
      `Personuppgiftsansvarig för de behandlingar som beskrivs i denna policy är:\n\n${companyInfo.companyName}\nOrg.nr ${companyInfo.orgNumber}\nAdress: ${companyInfo.address}\nE-post: ${companyInfo.email}`,
    ],
  },
  {
    id: "kontaktuppgifter",
    title: "3. Kontaktuppgifter",
    content: [
      `Du kan kontakta oss angående personuppgifter och integritetsfrågor via e-post: ${companyInfo.email}.`,
    ],
  },
  {
    id: "ansvarig-eller-bitrade",
    title: "4. När vi är personuppgiftsansvariga och när vi är personuppgiftsbiträde",
    content: [
      `Vi är personuppgiftsansvariga för de uppgifter vi samlar in via kontaktformuläret på webbplatsen, för hantering av kundkonton, och för kundadministration. När det gäller de uppgifter som kunden själv matar in i menyhanteringssystemet (exempelvis namn på personal, leverantörskontakter etc.) agerar vi som personuppgiftsbiträde på kundens uppdrag. Kunden ansvarar i dessa fall för att det finns rättslig grund för behandlingen.`,
    ],
  },
  {
    id: "vilka-uppgifter",
    title: "5. Vilka personuppgifter vi behandlar",
    content: [
      `Vi kan behandla följande kategorier av personuppgifter:\n\n• Kontaktuppgifter: namn, e-postadress, restaurangnamn (via kontaktformulär)\n• Kontouppgifter: användarnamn och inloggningsuppgifter\n• Menydata: information som kunden matar in i systemet, vilken i undantagsfall kan innehålla personuppgifter\n• Kundadministration: e-postadresser och annan kontaktinformation som förekommer i kundhantering och intern dokumentation\n• Teknisk information: cookies och annan teknisk data i enlighet med vår cookiepolicy`,
    ],
  },
  {
    id: "hur-vi-samlar-in",
    title: "6. Hur vi samlar in personuppgifter",
    content: [
      `Vi samlar in personuppgifter på följande sätt:\n\n• Direkt från dig, när du fyller i kontaktformuläret på vår webbplats\n• När vi skapar ett kundkonto åt dig\n• Genom din användning av tjänsten, exempelvis de uppgifter du matar in i menyhanteringssystemet\n• Genom cookies och liknande teknik på vår webbplats`,
    ],
  },
  {
    id: "kontaktformular",
    title: "7. Kontaktformulär",
    content: [
      `Via kontaktformuläret på vår webbplats samlar vi in namn, restaurangnamn och e-postadress. Dessa uppgifter används för att besvara din förfrågan och, om relevant, för att följa upp med information om våra tjänster. Den rättsliga grunden är berättigat intresse (att besvara och hantera affärsförfrågningar).`,
    ],
  },
  {
    id: "kundkonto",
    title: "8. Kundkonto och inloggning",
    content: [
      `Kundkonton skapas av ${companyInfo.serviceName} åt kunden. Vi lagrar användarnamn och lösenord (i krypterad form) för att möjliggöra säker inloggning. Den rättsliga grunden är fullgörande av avtal.`,
    ],
  },
  {
    id: "data-i-menyhanteringen",
    title: "9. Data i menyhanteringen",
    content: [
      `Kunden matar in information i menyhanteringssystemet, såsom namn på rätter, ingredienser, komponenter och leverantörsuppgifter. Denna data kan i enstaka fall innehålla personuppgifter. ${companyInfo.companyName} agerar som personuppgiftsbiträde för dessa uppgifter, och kunden är personuppgiftsansvarig.`,
    ],
  },
  {
    id: "kundadministration",
    title: "10. Kundadministration och dokumentation",
    content: [
      `E-postadresser och kontaktuppgifter till kunder kan lagras som en del av vår kundhantering och interna dokumentation, exempelvis i samband med fakturering, support och avtalshantering.`,
    ],
  },
  {
    id: "andamal",
    title: "11. Ändamål med behandlingen",
    content: [
      `Vi behandlar personuppgifter för följande ändamål:\n\n• Tillhandahålla och administrera tjänsten\n• Hantera kundkonton och inloggning\n• Besvara förfrågningar via kontaktformuläret\n• Kundadministration, fakturering och support\n• Förbättra och vidareutveckla tjänsten\n• Uppfylla rättsliga skyldigheter`,
    ],
  },
  {
    id: "rattslig-grund",
    title: "12. Rättslig grund",
    content: [
      `Vi baserar vår behandling av personuppgifter på följande rättsliga grunder:\n\n• Fullgörande av avtal – för att tillhandahålla tjänsten och hantera kundkonton\n• Berättigat intresse – för att besvara kontaktformulär, förbättra tjänsten och hantera kundrelationer\n• Rättslig förpliktelse – för att uppfylla bokföringskrav och andra lagstadgade skyldigheter\n• Samtycke – för icke-nödvändiga cookies, i enlighet med vår cookiepolicy`,
    ],
  },
  {
    id: "delning-med-leverantorer",
    title: "13. Delning av personuppgifter med leverantörer",
    content: [
      `Vi kan dela personuppgifter med tredje parter som hjälper oss att tillhandahålla tjänsten, exempelvis:\n\n• Infrastruktur- och plattformsleverantörer (för hosting, databas och teknisk drift)\n• Faktureringstjänster\n\nVi ingår personuppgiftsbiträdesavtal med samtliga leverantörer som behandlar personuppgifter på vårt uppdrag.`,
    ],
  },
  {
    id: "overforing-utanfor-eu",
    title: "14. Överföring utanför EU/EES",
    content: [
      `Personuppgifter kan komma att överföras till eller behandlas i länder utanför EU/EES genom våra infrastruktur- och plattformsleverantörer. I sådana fall vidtar vi lämpliga skyddsåtgärder, exempelvis genom att säkerställa att leverantören omfattas av EU-kommissionens standardavtalsklausuler eller andra godkända överföringsmekanismer, i enlighet med GDPR kapitel V.`,
    ],
  },
  {
    id: "lagringstid",
    title: "15. Lagringstid",
    content: [
      `Vi lagrar personuppgifter så länge det är nödvändigt för det ändamål för vilket de samlades in:\n\n• Kontaktformulär: uppgifter lagras så länge det krävs för att hantera förfrågan och eventuell uppföljning, dock som längst tolv (12) månader efter senaste kontakt om inget kundförhållande uppstår\n• Kundkonton: så länge kundförhållandet varar, samt en rimlig period därefter för att hantera eventuella kvarstående ärenden\n• Menydata: så länge kundkontot är aktivt\n• Kundadministration: i enlighet med tillämplig bokföringslagstiftning, normalt sju (7) år för bokföringsmaterial`,
    ],
  },
  {
    id: "sakerhet",
    title: "16. Säkerhet",
    content: [
      `Vi vidtar lämpliga tekniska och organisatoriska åtgärder för att skydda personuppgifter mot obehörig åtkomst, förlust och förstörelse. Detta inkluderar krypterade anslutningar, lösenordshashning och tillgångskontroller.`,
    ],
  },
  {
    id: "dina-rattigheter",
    title: "17. Dina rättigheter",
    content: [
      `Enligt GDPR har du följande rättigheter:\n\n• Rätt till tillgång – att begära information om vilka personuppgifter vi behandlar om dig\n• Rätt till rättelse – att begära att felaktiga uppgifter korrigeras\n• Rätt till radering – att under vissa förutsättningar begära att dina uppgifter raderas\n• Rätt till begränsning – att begära att behandlingen begränsas\n• Rätt till dataportabilitet – att få ut dina uppgifter i ett strukturerat, maskinläsbart format\n• Rätt att invända – att invända mot behandling som grundar sig på berättigat intresse\n\nFör att utöva dina rättigheter, kontakta oss via ${companyInfo.email}.`,
    ],
  },
  {
    id: "klagomal-till-imy",
    title: "18. Klagomål till IMY",
    content: [
      `Om du anser att vi behandlar dina personuppgifter i strid med dataskyddslagstiftningen har du rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY).\n\nIntegritetsskyddsmyndigheten\nBox 8114\n104 20 Stockholm\nwww.imy.se`,
    ],
  },
  {
    id: "cookies",
    title: "19. Cookies och liknande teknik",
    content: [
      `Vi använder cookies på vår webbplats. Mer information om vilka cookies vi använder och hur du kan hantera dina val hittar du i vår cookiepolicy.`,
    ],
  },
  {
    id: "andringar-i-policyn",
    title: "20. Ändringar i policyn",
    content: [
      `Vi kan komma att uppdatera denna integritetspolicy. Vid väsentliga förändringar informerar vi berörda parter. Den senaste versionen finns alltid tillgänglig på vår webbplats.`,
    ],
  },
  {
    id: "kontakt-privacy",
    title: "21. Kontakt",
    content: [
      `Vid frågor om denna integritetspolicy eller vår behandling av personuppgifter, kontakta oss på:\n\n${companyInfo.companyName}\nOrg.nr ${companyInfo.orgNumber}\nAdress: ${companyInfo.address}\nE-post: ${companyInfo.email}`,
    ],
  },
];

// ─── Cookie Policy (/cookiepolicy) ──────────────────────────────────────────

export const cookieSections: LegalSectionData[] = [
  {
    id: "vad-cookies-ar",
    title: "1. Vad cookies är",
    content: [
      `Cookies är små textfiler som lagras på din enhet (dator, surfplatta eller mobiltelefon) när du besöker en webbplats. De används för att webbplatsen ska fungera korrekt, för att förbättra användarupplevelsen och i vissa fall för att samla in statistik eller möjliggöra marknadsföring.`,
    ],
  },
  {
    id: "vilka-typer",
    title: "2. Vilka typer av cookies vi använder",
    content: [
      `Vi delar in våra cookies i tre kategorier: nödvändiga cookies, statistikcookies och marknadsföringscookies. Nedan beskrivs varje kategori närmare.`,
    ],
  },
  {
    id: "nodvandiga-cookies",
    title: "3. Nödvändiga cookies",
    content: [
      `Nödvändiga cookies krävs för att webbplatsen ska fungera korrekt. De möjliggör grundläggande funktioner som sidnavigering, inloggning och lagring av dina cookieval. Dessa cookies kan inte inaktiveras.`,
      `Exempel:\n• Sessionscookies för inloggning\n• Cookie för att lagra ditt samtycke avseende cookies`,
    ],
  },
  {
    id: "statistikcookies",
    title: "4. Statistikcookies",
    content: [
      `Statistikcookies hjälper oss att förstå hur besökare interagerar med webbplatsen genom att samla in anonym, aggregerad information. Dessa cookies kräver ditt samtycke.`,
      `I dagsläget har vi inga aktiva statistiktjänster implementerade, men infrastrukturen finns förberedd för framtida användning. Om och när statistikcookies aktiveras kommer denna policy att uppdateras med specifik information om vilka tjänster som används.`,
    ],
  },
  {
    id: "marknadsforingscookies",
    title: "5. Marknadsföringscookies",
    content: [
      `Marknadsföringscookies kan användas för att visa relevanta annonser och mäta effektiviteten av marknadsföringskampanjer. Dessa cookies kräver ditt samtycke.`,
      `I dagsläget har vi inga aktiva marknadsföringstjänster implementerade, men infrastrukturen finns förberedd för framtida användning. Om och när marknadsföringscookies aktiveras kommer denna policy att uppdateras med specifik information.`,
    ],
  },
  {
    id: "hur-samtycke-fungerar",
    title: "6. Hur samtycke fungerar",
    content: [
      `När du besöker vår webbplats för första gången visas en banner där du kan välja att acceptera alla cookies, bara nödvändiga cookies, eller anpassa dina val. Ditt val sparas lokalt i din webbläsare.`,
      `Nödvändiga cookies aktiveras alltid och kräver inget samtycke. Statistik- och marknadsföringscookies aktiveras först efter att du har gett ditt samtycke.`,
    ],
  },
  {
    id: "andra-sina-val",
    title: "7. Hur du ändrar dina val",
    content: [
      `Du kan när som helst ändra dina cookieinställningar via länken "Cookieinställningar" i sidfoten på vår webbplats. Du kan även radera cookies direkt i din webbläsares inställningar.`,
    ],
  },
  {
    id: "kontakt-cookie",
    title: "8. Kontakt",
    content: [
      `Vid frågor om vår användning av cookies, kontakta oss på:\n\n${companyInfo.companyName}\nOrg.nr ${companyInfo.orgNumber}\nE-post: ${companyInfo.email}`,
    ],
  },
];
